import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, MagnifyingGlassIcon, CheckCircleIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/solid';
import { toast } from 'sonner';
import { TemplateTasksClient } from '../../web-api-client';
import { API_CONFIG } from '../../config/api';
import { createAuthenticatedFetch } from '../../utils/apiClient';

interface AddTaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (taskData: AddTaskData) => Promise<void>;
    title?: string;
}

export interface AddTaskData {
    templateTaskId: number;
    taskName?: string;
    description?: string;
    categoryId?: number;
    categoryName?: string;
}

interface TemplateTask {
    id: number;
    taskName: string;
    description?: string;
    categoryId?: number;
    categoryName?: string;
    defaultTeamId?: number;
    defaultTeamName?: string;
    defaultPicId?: string;
}

interface GroupedTasks {
    [categoryName: string]: TemplateTask[];
}

export default function AddTaskModal({
    isOpen,
    onClose,
    onSave,
    title = "Add Task from Template"
}: AddTaskModalProps) {
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [templateTasks, setTemplateTasks] = useState<TemplateTask[]>([]);
    const [selectedTask, setSelectedTask] = useState<TemplateTask | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

    useEffect(() => {
        if (isOpen) {
            resetForm();
            fetchData();
        }
    }, [isOpen]);

    const resetForm = () => {
        setSelectedTask(null);
        setSearchQuery('');
        setExpandedCategories(new Set());
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const tasksData = await new TemplateTasksClient(API_CONFIG.baseUrl, createAuthenticatedFetch())
                .getTemplateTasks(undefined, undefined, true);

            const tasks = tasksData.map(t => ({
                id: t.id ?? 0,
                taskName: t.taskName ?? '',
                description: t.description,
                categoryId: t.templateCategoryId,
                categoryName: t.templateCategoryName || 'Uncategorized',
                defaultTeamId: t.defaultTeamId,
                defaultTeamName: undefined,
                defaultPicId: t.defaultPicId
            }));

            setTemplateTasks(tasks);

            // Expand all categories by default
            const categoryNames = new Set(tasks.map(t => t.categoryName || 'Uncategorized'));
            setExpandedCategories(categoryNames);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to load template tasks');
        } finally {
            setLoading(false);
        }
    };

    // Group tasks by category
    const groupedTasks = useMemo((): GroupedTasks => {
        const filtered = templateTasks.filter(task =>
            task.taskName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            task.description?.toLowerCase().includes(searchQuery.toLowerCase())
        );

        return filtered.reduce((groups, task) => {
            const category = task.categoryName || 'Uncategorized';
            if (!groups[category]) {
                groups[category] = [];
            }
            groups[category].push(task);
            return groups;
        }, {} as GroupedTasks);
    }, [templateTasks, searchQuery]);

    const toggleCategory = (categoryName: string) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryName)) {
                newSet.delete(categoryName);
            } else {
                newSet.add(categoryName);
            }
            return newSet;
        });
    };

    const handleTaskSelect = (task: TemplateTask) => {
        setSelectedTask(task);
    };

    const handleSave = async () => {
        if (!selectedTask) {
            toast.error('Please select a task template');
            return;
        }

        const taskData: AddTaskData = {
            templateTaskId: selectedTask.id,
            taskName: selectedTask.taskName,
            description: selectedTask.description,
            categoryId: selectedTask.categoryId,
            categoryName: selectedTask.categoryName
        };

        try {
            setSaving(true);
            await onSave(taskData);
            toast.success('Task added successfully');
            onClose();
        } catch (error) {
            console.error('Failed to add task:', error);
            toast.error('Failed to add task');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    const categoryNames = Object.keys(groupedTasks).sort();
    const totalTasks = templateTasks.length;
    const filteredCount = Object.values(groupedTasks).flat().length;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden transform transition-all">

                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-800">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-xl">
                                    <ClipboardDocumentListIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {title}
                                    </h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        Select a task from the template library
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <XMarkIcon className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex-1 flex items-center justify-center py-16">
                            <div className="text-center">
                                <div className="inline-block animate-spin rounded-full h-10 w-10 border-4 border-blue-200 border-t-blue-600"></div>
                                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading templates...</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Search Bar */}
                            <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                    <input
                                        type="text"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search templates..."
                                        className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white dark:placeholder-gray-400 transition-all"
                                    />
                                </div>
                                {searchQuery && (
                                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                        Showing {filteredCount} of {totalTasks} templates
                                    </p>
                                )}
                            </div>

                            {/* Task List */}
                            <div className="flex-1 overflow-y-auto px-6 py-4">
                                {categoryNames.length === 0 ? (
                                    <div className="text-center py-12">
                                        <ClipboardDocumentListIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                                        <p className="text-gray-500 dark:text-gray-400">No templates found</p>
                                        {searchQuery && (
                                            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                                                Try a different search term
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {categoryNames.map((categoryName) => (
                                            <div
                                                key={categoryName}
                                                className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
                                            >
                                                {/* Category Header */}
                                                <button
                                                    onClick={() => toggleCategory(categoryName)}
                                                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        {expandedCategories.has(categoryName) ? (
                                                            <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                                                        ) : (
                                                            <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                                                        )}
                                                        <span className="font-medium text-gray-700 dark:text-gray-200">
                                                            {categoryName}
                                                        </span>
                                                    </div>
                                                    <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded-full">
                                                        {groupedTasks[categoryName].length}
                                                    </span>
                                                </button>

                                                {/* Tasks in Category */}
                                                {expandedCategories.has(categoryName) && (
                                                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                                        {groupedTasks[categoryName].map((task) => (
                                                            <button
                                                                key={task.id}
                                                                onClick={() => handleTaskSelect(task)}
                                                                className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-all ${
                                                                    selectedTask?.id === task.id
                                                                        ? 'bg-blue-50 dark:bg-blue-900/30'
                                                                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
                                                                }`}
                                                            >
                                                                {/* Selection Indicator */}
                                                                <div className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                                                                    selectedTask?.id === task.id
                                                                        ? 'border-blue-500 bg-blue-500'
                                                                        : 'border-gray-300 dark:border-gray-500'
                                                                }`}>
                                                                    {selectedTask?.id === task.id && (
                                                                        <CheckCircleIcon className="h-4 w-4 text-white" />
                                                                    )}
                                                                </div>

                                                                {/* Task Info */}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className={`font-medium text-sm ${
                                                                        selectedTask?.id === task.id
                                                                            ? 'text-blue-700 dark:text-blue-300'
                                                                            : 'text-gray-800 dark:text-gray-200'
                                                                    }`}>
                                                                        {task.taskName}
                                                                    </p>
                                                                    {task.description && (
                                                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">
                                                                            {task.description}
                                                                        </p>
                                                                    )}
                                                                    {task.defaultTeamName && (
                                                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                                            👥 Default: {task.defaultTeamName}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-800">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {selectedTask ? '1 template selected' : 'No template selected'}
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={saving}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        disabled={saving || !selectedTask}
                                        onClick={handleSave}
                                        className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {saving ? (
                                            <span className="flex items-center gap-2">
                                                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                                </svg>
                                                Adding...
                                            </span>
                                        ) : (
                                            'Add Task'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
